import { Button, Center, Flex, Icon, Input } from '@chakra-ui/react';
import './AfterInterviewDetails.css';
import { useState, useRef, useEffect, useCallback, memo } from 'react';
import { InterviewAssistant } from '../component';
import type { InterviewAssistantHandle } from '../component/InterviewAssistant';
import { AiStar } from '@/assets/icons/interview';

export const AfterInterviewAssistant = memo(function AfterInterviewAssistant({
  interview,
  interviewId = undefined,
  triggerContainerProps = {},
  assistantContainerProps = {},
}: {
  interview: any;
  interviewId?: string | number | undefined;
  triggerContainerProps?: Record<string, any>;
  assistantContainerProps?: Record<string, any>;
}) {
  const [assistantLoading, setAssistantLoading] = useState<boolean>(false);
  const [externalPrompt, setExternalPrompt] = useState<string>('');
  const [queuedPrompt, setQueuedPrompt] = useState<string>('');

  const [showInterviewAssistant, setShowInterviewAssistant] =
    useState<boolean>(false);

  const interviewAssistantRef = useRef<HTMLDivElement>(null);
  const assistantApiRef = useRef<InterviewAssistantHandle | null>(null);

  const handleCloseAssistant = useCallback(() => {
    setShowInterviewAssistant(false);
  }, []);

  const handleLoadingChange = useCallback((loading: boolean) => {
    setAssistantLoading(loading);
  }, []);

  // When assistant becomes visible and there is a queued prompt, send it
  useEffect(() => {
    if (showInterviewAssistant && queuedPrompt && assistantApiRef.current) {
      assistantApiRef.current.postMessage(queuedPrompt);
      setQueuedPrompt('');
    }
  }, [showInterviewAssistant, queuedPrompt]);

  const handleSubmitPrompt = useCallback(() => {
    setExternalPrompt((prev) => {
      const message = prev.trim();
      if (!message) return prev;
      if (assistantApiRef.current && showInterviewAssistant) {
        assistantApiRef.current.postMessage(message);
      } else {
        setQueuedPrompt(message);
        setShowInterviewAssistant(true);
      }
      return '';
    });
  }, [showInterviewAssistant]);

  const handleOpenAssistant = useCallback(() => {
    setShowInterviewAssistant(true);
  }, []);

  return (
    <>
      <Flex
        position="absolute"
        top="0"
        left="0"
        right="0"
        bottom="54px"
        bg="white"
        zIndex="1"
        overflow="hidden"
        background="linear-gradient( #fff, #fff) padding-box,linear-gradient(180deg, #8A90FF 0%, #B15FDA 100%)"
        border="1px solid transparent"
        borderRadius="xs"
        visibility={showInterviewAssistant ? 'visible' : 'hidden'}
        opacity={showInterviewAssistant ? 1 : 0}
        transition="opacity 0.2s ease-in-out"
        boxShadow="32px 68px 30px rgba(191, 195, 223, 0.01), 18px 38px 25px rgba(191, 195, 223, 0.05), 8px 17px 19px rgba(191, 195, 223, 0.09), 2px 4px 10px rgba(191, 195, 223, 0.1);"
        ref={interviewAssistantRef}
		   {...assistantContainerProps}
      >
        <InterviewAssistant
          interview={interview}
          interviewId={interviewId}
          onClose={handleCloseAssistant}
          showFooter={false}
          ref={assistantApiRef}
          onLoadingChange={handleLoadingChange}
        />
      </Flex>

			{/* trigger container */}
      <Flex
        mt="12px"
        alignItems="center"
        justifyContent="space-between"
        p="6px"
        background="linear-gradient( #fff, #fff) padding-box,linear-gradient(180deg, #8A90FF 0%, #B15FDA 100%)"
        border="1px solid transparent"
        borderRadius="xs"
        data-testid="interview-assistant-cta"
        onClick={handleOpenAssistant}
        gap="6px"
        filter="drop-shadow(0px 71px 29px rgba(177, 95, 218, 0.01)) drop-shadow(0px 40px 24px rgba(177, 95, 218, 0.05)) drop-shadow(0px 18px 18px rgba(177, 95, 218, 0.09)) drop-shadow(0px 4px 10px rgba(177, 95, 218, 0.1))"
        pl="12px"
				{...triggerContainerProps}
      >
        <Center>
          <Icon as={AiStar} w="16px" h="16px" />
        </Center>
        <Input
          placeholder="Ask our AI assistant for anything"
          value={externalPrompt}
          onChange={(e) => setExternalPrompt(e.target.value)}
          onFocus={handleOpenAssistant}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              handleSubmitPrompt();
            }
          }}
          border="none"
          outline="none"
          boxShadow="none"
          _hover={{ boxShadow: 'none', border: 'none' }}
          _active={{ boxShadow: 'none', border: 'none' }}
          _focus={{ boxShadow: 'none', border: 'none', outline: 'none' }}
          isDisabled={assistantLoading && showInterviewAssistant}
          h="30px"
          px="0px"
        />
        <Button
          onClick={handleSubmitPrompt}
          variant="primary"
          _active={{}}
          _focus={{}}
          background="linear-gradient(180deg, #8A90FF 0%, #9582F5 25.48%, #9F76EB 50%, #A86BE3 75%, #B15FDA 100%), #FFFFFF"
          _hover={{
            background:
              'linear-gradient(180deg, #8288F7 0%, #8D7AEF 25.48%, #976EE3 50%, #A063DB 75%, #A957D2 100%), #FFFFFF',
          }}
          isDisabled={assistantLoading && showInterviewAssistant}
        >
          Ask AI
        </Button>
      </Flex>
    </>
  );
});
